import React, { useContext, useEffect } from "react";
import styled from "@emotion/styled";
import { Header } from "./Header/Header";
import { useAuth } from "@/providers/AuthenticationProvider";
import { useRouter } from "next/router";
import { theme } from "@/config/theme";
import { DESKTOP_HEADER_MEDIA_QUERY } from "./Header/HeaderConstants";

import backgroundImageUrl from "@/assets/images/background.png";
import Typography from "@digital-hig/mui/Typography";
import { Helmet } from "react-helmet-async";
import { getItems } from "@/service/itemService";
import { clearPLCs, storePLCs } from "@/utilities/localStorageUtils";
import { AppContext } from "@/providers/AppContext";
import { getJobMetaDataApiConfig } from "@/api/jobs/apiConfig";
import { isAppRunningOnLocalhost } from "@/utilities/getEnvironment";
import { readSessionCache, SESSION_CACHE_KEYS, subscribeSessionCache, writeSessionCache } from "@/utilities/auth/sessionCache";
import axios from "axios";

type SessionPermissions = { admin: boolean; btdUser: boolean };

// The publisher metadata call targets different hosts in cooperate mode (see getJobMetaDataApiConfig),
// so cache per env + mode.
const getPermissionsCacheScope = (apiEnv: string) => `${apiEnv}:${sessionStorage.getItem("copMode") ? "cop" : "com"}`;

const LayoutWrapper = styled.div({
  display: "grid",
  gridTemplateRows: "auto 1fr auto",
  gridTemplateColumns: "100vw"
  //   minHeight: "100vh",
});

const WidthContainer = styled.div({
  marginLeft: "auto",
  marginRight: "auto",
  width: "100%",
  paddingLeft: theme.primitives.spacings[4],
  paddingRight: theme.primitives.spacings[4],
  // margin: "50px auto",
  maxWidth: "calc(1440px + (4rem * 2))",
  [DESKTOP_HEADER_MEDIA_QUERY]: {
    maxWidth: `calc(${theme.primitives.grid["max-width"]} + (${theme.primitives.spacings[9]} * 2))`,
    paddingLeft: theme.primitives.spacings[9],
    paddingRight: theme.primitives.spacings[9]
  }
});

export function PageLayout({ children, stickyHeader }: { children?: React.ReactNode; stickyHeader: boolean }) {
  const { pathname } = useRouter();
  const { authStatus, accessToken, vpnConnect, apiEnv, userProfile } = useAuth();
  const { setInitPLC, setAdmin, setIsBtdUser } = useContext(AppContext);

  useEffect(() => {
    if (accessToken) {
      init();
    }
  }, [accessToken]);

  const init = async () => {
    clearPLCs();
    const plcs = await getItems(apiEnv);
    storePLCs(plcs);
    setInitPLC(true);
  };

  // check permission start
  // Cached flags are applied optimistically so a new tab renders admin/BTD UI right away; the
  // metadata call still runs on every load and its result is shared with the other open tabs.
  useEffect(() => {
    if (isAppRunningOnLocalhost()) {
      setAdmin(true);
      setIsBtdUser(true);
      return;
    }
    if (accessToken && userProfile) {
      const scope = getPermissionsCacheScope(apiEnv);
      const cached = readSessionCache<SessionPermissions>(SESSION_CACHE_KEYS.PERMISSIONS, userProfile.userId, scope);
      if (cached) {
        setAdmin(cached.admin);
        setIsBtdUser(cached.btdUser);
      }

      var config = getJobMetaDataApiConfig(apiEnv, userProfile?.emailId, "publisher", accessToken);
      axios(config)
        .then((response) => {
          const permissions: SessionPermissions = {
            admin: response.data?.admin === true,
            btdUser: response.data?.btdUser === true
          };
          setAdmin(permissions.admin);
          setIsBtdUser(permissions.btdUser);
          writeSessionCache(SESSION_CACHE_KEYS.PERMISSIONS, userProfile.userId, scope, permissions);
        })
        .catch((error) => { });
    }
  }, [accessToken, userProfile, apiEnv]);

  useEffect(() => {
    if (isAppRunningOnLocalhost() || !userProfile?.userId) return;
    return subscribeSessionCache<SessionPermissions>(
      SESSION_CACHE_KEYS.PERMISSIONS,
      userProfile.userId,
      getPermissionsCacheScope(apiEnv),
      (permissions) => {
        setAdmin(permissions.admin);
        setIsBtdUser(permissions.btdUser);
      }
    );
  }, [userProfile, apiEnv, setAdmin, setIsBtdUser]);

  // check permission end

  return (
    <LayoutWrapper style={authStatus !== "logged-in" || !accessToken || vpnConnect == 0 || vpnConnect == -1 ? { height: "100vh" } : {}}>
      <Header sticky={stickyHeader} />
      {authStatus !== "logged-in" || !accessToken || vpnConnect == 0 || vpnConnect == -1 ? (
        <div className="loginBackground" style={{ backgroundImage: `url(${backgroundImageUrl})` }}>
          {pathname.includes("/login") && children}
        </div>
      ) : (
        <div>
          <Helmet>
            <script type="text/javascript">
              {`
            (function(){var g=function(e,h,f,g){
              this.get=function(a){for(var a=a+"=",c=document.cookie.split(";"),b=0,e=c.length;b<e;b++){for(var d=c[b];" "==d.charAt(0);)d=d.substring(1,d.length);if(0==d.indexOf(a))return d.substring(a.length,d.length)}return null};
              this.set=function(a,c){var b="",b=new Date;b.setTime(b.getTime()+6048E5);b="; expires="+b.toGMTString();document.cookie=a+"="+c+b+"; path=/; "};
              this.check=function(){var a=this.get(f);if(a)a=a.split(":");else if(100!=e)"v"==h&&(e=Math.random()>=e/100?0:100),a=[h,e,0],this.set(f,a.join(":"));else return!0;var c=a[1];if(100==c)return!0;switch(a[0]){case "v":return!1;case "r":return c=a[2]%Math.floor(100/c),a[2]++,this.set(f,a.join(":")),!c}return!0};
              this.go=function(){if(this.check()){var a=document.createElement("script");a.type="text/javascript";a.src=g;document.body&&document.body.appendChild(a)}};
              this.start=function(){var t=this;"complete"!==document.readyState?window.addEventListener?window.addEventListener("load",function(){t.go()},!1):window.attachEvent&&window.attachEvent("onload",function(){t.go()}):t.go()};};
            try{(new g(100,"r","QSI_S_ZN_b2b6Wn008pBGKWN","https://znb2b6wn008pbgkwn-autodeskfeedback.siteintercept.qualtrics.com/SIE/?Q_ZID=ZN_b2b6Wn008pBGKWN")).start()}catch(i){}})();
          `}
            </script>
          </Helmet>
          <div id="ZN_b2b6Wn008pBGKWN"></div>
          {!pathname.includes("/login") && <WidthContainer>{children}</WidthContainer>}
        </div>
      )}
    </LayoutWrapper>
  );
}
